/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import { Check, ChevronDown, ChevronUp, Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { useState } from 'react';
import { truncate } from 'lodash';

// Utils
import { cn } from '@/lib/utils';

// Components
import { Button } from '@/components/UI/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/UI/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/UI/Popover';
import { Label } from '../UI/Label';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export type ComboboxItem = {
  value: string;
  label: string;
};

export type ComboboxProps = {
  items: ComboboxItem[];
  label?: string;
  error?: FieldError;
  register?: UseFormRegisterReturn;
  selectedItem?: ComboboxItem;
  onSelect?: (item: ComboboxItem) => void;
  placeholder?: string;
  onAddNew?: (newItem: string) => void;
  isAddingNewValue?: boolean;
  triggerClassName?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onSeachChange?: (query: string) => void;
  isLoading?: boolean;
};

export const Combobox: React.FC<ComboboxProps> = ({
  items,
  label,
  error,
  register,
  selectedItem,
  onSelect,
  placeholder,
  onAddNew,
  isAddingNewValue,
  triggerClassName,
  disabled,
  icon,
  onSeachChange,
  isLoading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  const handleSelect = (item: ComboboxItem) => {
    if (onSelect) {
      onSelect(item);
    }
    setOpen(false);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onSeachChange?.(value);
  };

  return (
    <div className={cn('flex flex-col', disabled ? 'cursor-not-allowed' : '')}>
      <Label className="text-bg-blue font-semibold text-background-1 flex items-center">
        {icon && <div className="mr-2 mb-1">{icon}</div>}
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="overflow-hidden text-ellipsis">
          <Button
            variant="ghost"
            role="combobox"
            type="button"
            aria-expanded={open}
            className={cn(
              'w-full justify-between h-[53px] rounded-t-sm focus-visible:outline-none bg-gray-200 rounded-b-none hover:border-b-2 hover:border-gray-400 ',
              {
                'border-b-2 border-destructive': error,
              },
              triggerClassName
            )}
            disabled={disabled}
          >
            <div
              className={cn('text-background-1', disabled ? 'text-black' : '')}
            >
              {selectedItem
                ? selectedItem.label
                : t('global.select_item', placeholder || 'Select item...')}
            </div>
            {!open ? (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            ) : (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-full p-0">
          <Command>
            <CommandInput
              placeholder={t('global.search', 'Search...')}
              onValueChange={handleInputChange}
            />
            <CommandList>
              {!isLoading && (
                <CommandEmpty>
                  <div className="flex flex-col items-center ">
                    {t('global.no_results', 'No item found.')}
                  </div>
                </CommandEmpty>
              )}

              {isLoading ? (
                <div className="flex justify-center items-center py-2">
                  <Loader2 className="animate-spin h-5 w-5 text-gray-500" />
                </div>
              ) : (
                <CommandGroup>
                  {items.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.label}
                      onSelect={() => handleSelect(item)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedItem?.value === item.value
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {item.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {onAddNew && inputValue.length > 0 && (
                <div className="flex flex-col items-center mt-2 pb-2">
                  {items.some(
                    (item) =>
                      item.label.toLowerCase().trim() ===
                      inputValue.toLowerCase().trim()
                  ) ? null : (
                    <Button
                      variant="default"
                      type="button"
                      className="max-w-full flex flex-wrap text-left"
                      isLoading={isAddingNewValue}
                      onClick={() => onAddNew(inputValue)}
                    >
                      <Plus /> {t('global.add', 'Add: ')}
                      {truncate(inputValue, { length: 20 })}
                    </Button>
                  )}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <input
        type="hidden"
        value={selectedItem?.value || ''}
        {...register}
        aria-invalid={!!error}
      />
      {error && (
        <p className="ml-2 mt-1 text-xs text-destructive">
          {t(error.message || '')}
        </p>
      )}
    </div>
  );
};

Combobox.displayName = 'Combobox';
